import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/api";
import { ROLE_BADGE, EVENT_STATUS } from "@/lib/format";
import {
  Calendar, BookMarked, Users, ShoppingCart, CheckSquare, PackageCheck,
  ArrowRightLeft, DollarSign, Boxes, LogOut, Plane, UserCog, History, KeyRound, Receipt,
} from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EVENT_FREE = ["/events", "/customers", "/team"];

const TABS = [
  { to: "/events", label: "Events", icon: Calendar, tid: "nav-tab-events" },
  { to: "/catalog", label: "Katalog", icon: BookMarked, tid: "nav-tab-katalog" },
  { to: "/customers", label: "Customer", icon: Users, tid: "nav-tab-customers" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, tid: "nav-tab-orders" },
  { to: "/payments", label: "Pembayaran", icon: CheckSquare, tid: "nav-tab-payments" },
  { to: "/shopping-list", label: "Shopping List", icon: ShoppingCart, tid: "nav-tab-shopping-list" },
  { to: "/bought", label: "Bought", icon: Receipt, tid: "nav-tab-bought" },
  { to: "/allocation", label: "Alokasi", icon: PackageCheck, tid: "nav-tab-bought-allocation" },
  { to: "/substitution", label: "Substitusi", icon: ArrowRightLeft, tid: "nav-tab-substitusi" },
  { to: "/boxes", label: "Box / Kirim", icon: Boxes, tid: "nav-tab-boxes" },
  { to: "/cost-profit", label: "Biaya & Profit", icon: DollarSign, tid: "nav-tab-biaya-profit" },
  { to: "/activity", label: "Aktivitas", icon: History, tid: "nav-tab-activity" },
  { to: "/team", label: "Tim", icon: UserCog, tid: "nav-tab-team", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(localStorage.getItem("eventId") || "");
  const location = useLocation();
  const [pwRequests, setPwRequests] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const poll = () => api.get("/password-requests").then((r) => setPwRequests(r.data.length)).catch(() => {});
    poll();
    const id = setInterval(poll, 60000);
    window.addEventListener("pw-requests-changed", poll);
    return () => { clearInterval(id); window.removeEventListener("pw-requests-changed", poll); };
  }, [user?.role, location.pathname]);

  const loadEvents = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
    if (!data.length) {
      setEventId("");
      localStorage.removeItem("eventId");
    } else if (!data.find((e) => e.id === eventId)) {
      setEventId(data[0].id);
      localStorage.setItem("eventId", data[0].id);
    }
  };

  useEffect(() => { loadEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectEvent = (id) => {
    setEventId(id);
    localStorage.setItem("eventId", id);
  };

  const event = events.find((e) => e.id === eventId) || null;
  const visibleTabs = useMemo(() => TABS.filter((t) => !t.adminOnly || user?.role === "admin"), [user?.role]);
  const outletCtx = useMemo(() => ({ event, eventId, events, reloadEvents: loadEvents }), [event, eventId, events]); // eslint-disable-line react-hooks/exhaustive-deps

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-900 text-white grid place-items-center">
                <Plane className="w-4 h-4" />
              </div>
              <span className="font-extrabold tracking-tight text-slate-900 text-lg">JastipFlow</span>
            </div>
            <div className="hidden md:block w-px h-6 bg-slate-200" />
            <Select value={eventId} onValueChange={selectEvent}>
              <SelectTrigger data-testid="event-selector-dropdown" className="w-[280px] h-9 text-sm">
                <SelectValue placeholder="Pilih Event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`event-option-${e.code}`}>
                    <span className="font-mono">{e.code}</span> · {e.country}
                    {e.locked ? " 🔒" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {event && (
              <span className={`hidden lg:inline text-[11px] font-semibold uppercase px-2 py-0.5 rounded border ${EVENT_STATUS[event.status] || EVENT_STATUS.draft}`}>
                {event.status?.replace("_", " ")} · {event.currency}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-slate-800 leading-tight">{user?.name}</div>
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${ROLE_BADGE[user?.role] || "bg-slate-200"}`}>
                {user?.role === "ops" ? "OPS · SALES & SHOPPER" : user?.role?.toUpperCase()}
              </span>
            </div>
            <button data-testid="change-password-btn" title="Ubah password" onClick={() => setPwOpen(true)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
              <KeyRound className="w-4 h-4" />
            </button>
            <button data-testid="logout-btn" onClick={doLogout} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500">
              <LogOut className="w-4 h-4" />
            </button>
            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
          </div>
        </div>
        <nav className="flex items-center gap-1 px-2 overflow-x-auto border-t border-slate-100">
          {visibleTabs.map((t) => (
            <NavLink
              key={t.to} to={t.to} data-testid={t.tid}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
                }`
              }
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.to === "/team" && pwRequests > 0 && (
                <span data-testid="team-pending-badge" title={`${pwRequests} permintaan reset password`} className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold animate-pulse">{pwRequests}</span>
              )}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="p-3 md:p-4 max-w-[1600px] mx-auto">
        {!eventId && !EVENT_FREE.includes(location.pathname) ? (
          <div className="text-center py-20 text-slate-400" data-testid="no-event-placeholder">
            <div>Belum ada event. Buka tab <span className="font-semibold text-slate-600">Events</span> untuk membuat event.</div>
            <div className="text-xs mt-2">Halaman Events, Customer, dan Tim tetap bisa diakses tanpa event.</div>
          </div>
        ) : (
          <Outlet context={outletCtx} />
        )}
      </main>
    </div>
  );
}
